import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import OfferCalendarDayCell from './OfferCalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Heading from 'LuxKit/Typography/Heading'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import type { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import { getCalendarCheapestDays, markCheapestPrice } from 'selectors/offerPage/offerPageSelectors'
import useBundleAndSaveCalendarDayPickingUtils from 'hooks/Offers/BundleAndSave/useBundleAndSaveCalendarDayPickingUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { getBestVillasDiscounts } from '../../../Villas/utils/calculateDiscount'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { getComplementaryBundleState } from 'selectors/offerPage/bundledOfferPageSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five} inset,
    0 -1px 0 0 ${props => props.theme.palette.neutral.default.five} inset;
`

interface Props {
  days: Array<App.CalendarDay>
  tacticalDays: Array<string>
  selectedDay?: App.CalendarDay
  className?: string
  currentOfferId?: string
  bundleOfferId?: string
  duration: number
  cacheActive?: boolean
  otherAvailability: { [checkIn: string]: boolean }
  isRoomSelected?: boolean
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  isAnyFlightsActive: boolean
  complementaryBundleState?: BundledOfferState
  cheapestDays: Record<string, App.CalendarCheapestDay>
  offer: App.Offer
  displayMemberPrice: boolean
  rooms: Array<App.Occupants>
  markCheapestPrice: boolean
  canLoyaltyUpgrade?: boolean
  tier: App.LoyaltyTier
}

function checkHasPromo(day: App.CalendarDay) {
  return !!day.dealId && day.flightPrice > 0
}

function OfferCalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    tacticalDays,
    duration,
    rooms,
    enableCalendarRecommendations,
    otherAvailability,
    selectedDay,
    isRoomSelected,
    flightsOnly,
    isAnyFlightsActive,
    currentOfferId,
    bundleOfferId,
    complementaryBundleState,
    cheapestDays,
    offer,
    displayMemberPrice,
    markCheapestPrice,
    canLoyaltyUpgrade,
    tier,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getLocalDate(firstDate.checkIn).getDay() + 1,
  }), [firstDate])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate), [firstDate])

  const betweenRange = useMemo(() => {
    if (selectedDay) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(selectedDay.checkIn),
        getLocalDate(selectedDay.checkOut),
      ).range
    }
    return undefined
  }, [selectedDay, firstDate])

  const {
    isComplementaryBundleCell,
    isComplementaryCheckInCell,
    isComplementaryCheckOutCell,
    isIllegalBundleSelection,
  } = useBundleAndSaveCalendarDayPickingUtils(complementaryBundleState, duration)

  const onDayClick = useCallback((day: App.CalendarDay) => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day.checkIn).format(ISO_DATE_FORMAT))
  }, [onUserAnalyticsAction])

  const dispatch = useOfferPageDispatch()
  const onInvalidBundleDateClick = useCallback(
    () => {
      if (isBundleAndSaveV2Enabled()) {
        showSnackbar('Check-in date overlaps with your other stay. Please select a new date.', 'warning')
      } else {
        showSnackbar(
          'The dates for Hotel 2 must come after Hotel 1. Try swapping the order of the hotels.',
          'warning',
          {
            action: {
              label: 'Swap order',
              onAction: () => {
                dispatch({ type: OfferPageActions.openSwapModal })
              },
            },
          },
        )
      }
    },
    [dispatch],
  )

  const villasDiscounts = isVillaOffer(offer) ? getBestVillasDiscounts(offer.discounts) : undefined
  const hideChildPrices = offer.type === 'tactical_ao_hotel' && !!offer.property?.hideChildPrices && rooms?.some(room => room.children !== undefined && room.children > 0)

  return (
    <div className={className}>
      <BaseGrid>
        <Caption variant="large" as="abbr" title="Sunday">Sun</Caption>
        <Caption variant="large" as="abbr" title="Monday">Mon</Caption>
        <Caption variant="large" as="abbr" title="Tuesday">Tue</Caption>
        <Caption variant="large" as="abbr" title="Wednesday">Wed</Caption>
        <Caption variant="large" as="abbr" title="Thursday">Thu</Caption>
        <Caption variant="large" as="abbr" title="Friday">Fri</Caption>
        <Caption variant="large" as="abbr" title="Saturday">Sat</Caption>
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="neutral-four" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => <OfferCalendarDayCell
          key={day.checkIn}
          duration={duration}
          bundleOfferId={bundleOfferId}
          checkIn={day.checkIn}
          style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
          day={day}
          hasOtherAvailability={otherAvailability[day.checkIn] && !bundleOfferId}
          isCheckIn={day.checkIn === selectedDay?.checkIn}
          isCheckOut={day.checkIn === selectedDay?.checkOut}
          isBetweenDay={!!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))}
          isRoomSelected={isRoomSelected}
          flights={isAnyFlightsActive}
          onClick={onDayClick}
          flightsOnly={flightsOnly}
          currentOfferId={currentOfferId}
          enableCalendarRecommendations={enableCalendarRecommendations}
          isComplementaryBundleCell={isComplementaryBundleCell(day)}
          isComplementaryCheckIn={isComplementaryCheckInCell(day)}
          isComplementaryCheckOut={isComplementaryCheckOutCell(day)}
          isIllegalBundleSelection={isIllegalBundleSelection(day)}
          complementaryPropertyName={complementaryBundleState?.offer?.property?.name}
          hasPromo={checkHasPromo(day)}
          isTacticalPromotion={tacticalDays.includes(day.checkIn) && day.hotelAvailable}
          cheapestDay={cheapestDays[day.uniqueKey] ?? null}
          onInvalidBundleDateClick={onInvalidBundleDateClick}
          showUrgencyLabels={!isVillaOffer(offer)}
          soldOutText={isVillaOffer(offer) ? 'Check-in unavailable' : undefined}
          displayMemberPrice={displayMemberPrice}
          villasDiscounts={villasDiscounts}
          hideChildPrices={hideChildPrices}
          markCheapestPrice={markCheapestPrice}
          tier={tier}
          canLoyaltyUpgrade={canLoyaltyUpgrade}
        />)}
      </Grid>
    </div>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    offer: state.offer,
    bundleOfferId: state.bundleOffer?.id,
    duration: state.duration,
    rooms: state.rooms,
    currentOfferId: state.paginator?.currentOfferId,
    complementaryBundleState: getComplementaryBundleState(state),
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(state.offer) : false,
    isAnyFlightsActive: state.flights,
    cheapestDays: getCalendarCheapestDays(currentState, appState),
    displayMemberPrice: currentState.displayMemberPrice,
    markCheapestPrice: markCheapestPrice(state),
    open: !!state.activeFilter,
  }
}, OfferCalendarDaysMonth, preventRenderWithClosedFilter)
